import React, {useEffect, useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import { toast } from 'react-toastify';
import {useList} from 'react-use';
import {DebounceInput} from 'react-debounce-input';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import lodash from 'lodash';

import './style.scss';
import DropdownMenu from '../../../components/dropdown/DropdownMenu';
import Pagination from '../../../components/pagination';
import {getListMyTopic, createTopic, deleteTopic, copyTopic} from '../../../actions/my-topic';
import {getURLTopic} from '../../../actions/delivery';
import Loading from '../../../components/loading';
import usePagination from '../../../useHook/usePagination';
import useRole from '../../../useHook/useRole';
import TopicChooseUser from './modal/TopicChooseUser';
import TopicChooseTeam from './modal/TopicChooseTeam';
import TopicChooseGame from './modal/TopicChooseGame';
import TopicDelete from './modal/TopicDelete';
import TopicCopyWorkspace from './modal/TopicCopyWorkspace';

export const TOPIC_GAME = 'ゲームから作成';
export const TOPIC_TEAM = '球団から作成';
export const TOPIC_PLAYER = '選手から作成';

const ACTION_COPY_WORKSPACE = 'ワークスペースにコピー';
const ACTION_REPRODUCE = '複製';
const ACTION_EDIT = '編集';
const ACTION_DELETE = '削除';
const ACTION_COPY_DONE = 'ワークスペースにコピーしました';
const ACTION_REPRODUCE_DONE = 'MyTopicsにコピーしました';

function MyTopic() {
    const dispatch = useDispatch();
    const history = useHistory();
    const {setTotalRecords, currentPage, setCurrentPage, itemPerPage, totalPage} = usePagination(20);
    const role = useRole();
    const [keyword, setKeyword] = useState('');
    const [error, setError] = useState('');
    const [listTopic, {set, insertAt, sort, clear}] = useList([]);
    const [listCreateTopic] = useState([TOPIC_GAME, TOPIC_TEAM, TOPIC_PLAYER]);

    const [sortField, setSortField] = useState({
        field: '', order: 'desc'
    });

    const [headers] = useState([
        {
            title: 'タイトル',
            field: 'title',
            width: '15%',
        },
        {
            title: 'ワークスペース',
            field: 'workspace',
            width: '20%',
        },
        {
            title: 'カテゴリー',
            field: 'categoryName',
            width: '20%',
        },
        {
            title: '対象',
            field: 'name',
            width: '20%',
        },
        {
            title: '作成日時',
            field: 'created_at',
            width: '10%',
        },
        {
            title: '更新日時',
            field: 'updated_at',
            width: '10%',
        },
    ]);

    const [selectedTopic, setSelectedTopic] = useState('');
    const [selectedAction, setSelectedAction] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [createTopicLoading, setCreateTopicLoading] = useState(false);

    useEffect(() => {
        fetchListTopic();
    }, [keyword, currentPage]);

    const fetchListTopic = useCallback(async () => {
        setCreateTopicLoading(true);
        setError('');
        try {
            const response = await dispatch(getListMyTopic({
                keyword,
                page: currentPage,
                order: 'desc',
                sort: 'topics.updated_at',
            }));
            clear();
            set(response.data);
            setTotalRecords(response.meta.total);
            setCreateTopicLoading(false);
        }catch (error) {
            setCreateTopicLoading(false);
            setError('Server error');
        }
    }, [currentPage, keyword]);

    const onChangePage = useCallback((page) => {
        setCurrentPage(page)
    }, []);

    const onChooseOption = useCallback((option) => {
        setSelectedTopic(option);
    }, []);

    const onSortField = useCallback((field) => {
        const order = sortField.order === 'desc' ?  'asc' : 'desc';
        setSortField({
            field,
            order
        });
        set(lodash.orderBy(listTopic, [field], [order]));
    }, [sortField, listTopic]);

    const onChooseAction = useCallback((option, topic) => {
        if(option === ACTION_REPRODUCE) {
            onCopyTopic(topic, null);
            return;
        }
        if(option === ACTION_EDIT) {
            history.push(getURLTopic(topic));
            return;
        }
        setSelectedAction(option);
        setSelectedRow(topic);
    }, []);

    const onCreateTopic = useCallback(async (value) => {
        let params = {};
        let url = '';
        if(selectedTopic === TOPIC_PLAYER) {
            params.player_id = value.id;
            url = `/player/${value.id}`;
        }
        if(selectedTopic === TOPIC_TEAM) {
            params.team_id = value.id;
            url = `/team-report/${value.id}`;
        }
        if(selectedTopic === TOPIC_GAME) {
            params.game_id = value.id;
            const date = moment(value.game_date, 'YYYY-MM-DD').format('YYYYMMDD');
            url = `/game/${value.id}/${value.home_team.id}/${date}`;
        }
        try {
            setCreateTopicLoading(true);
            const {data} = await dispatch(createTopic(params));
            setSelectedTopic('');
            insertAt(0, data);
            setCreateTopicLoading(false);
            fetchListTopic();
            if(url) {
                history.push(url + `/topic/${data.id}`);
            }
        }catch (error) {
            setCreateTopicLoading(false);
            toast.error(error.response.data.message)
        }
    }, [selectedTopic, listTopic]);

    const onCopyTopic = useCallback(async (topic, workspace) => {
        const params = {
            topicId : topic.id,
            workspace_id: workspace ? workspace.id: -1
        };
        try {
            setCreateTopicLoading(true);
            const {data} = await dispatch(copyTopic(params));
            setSelectedTopic('');
            insertAt(0, data);
            setCreateTopicLoading(false);
            toast.success(ACTION_REPRODUCE_DONE);
        }catch (error) {
            setCreateTopicLoading(false);
            toast.error(error.response.data.message)
        }
    }, [selectedTopic, listTopic]);

    const onCloseModal = useCallback(() => {
        setSelectedTopic('');
    }, []);

    const onCloseActionModal = useCallback(() => {
        setSelectedAction('');
    }, []);

    const onDeleteTopic = useCallback(async () => {
        try {
            setSelectedAction('');
            setCreateTopicLoading(true);
            await dispatch(deleteTopic(selectedRow.id));
            setCreateTopicLoading(false);
            fetchListTopic();
        }catch (error) {
            setCreateTopicLoading(false);
            toast.error(error.response.data.message)
        }
    }, [selectedRow]);

    const onSaveCopyWorkspace = useCallback(async (workspace) => {
        setCreateTopicLoading(true);
        await dispatch(copyTopic({
            topicId: selectedRow.id,
            workspace_id: workspace.id,
        }));
        setSelectedAction('');
        fetchListTopic();
        setCreateTopicLoading(false);
        toast.success(ACTION_COPY_DONE);
    }, [selectedRow]);

    const renderListWithRole = useCallback((topic, role) => {
        if(role.role) {
            return [ACTION_COPY_WORKSPACE, ACTION_REPRODUCE, ACTION_EDIT, ACTION_DELETE];
        }
        if(topic.workspace && topic.workspace.delivery_at) {
            return [ACTION_REPRODUCE];
        }
        return [ACTION_REPRODUCE, ACTION_EDIT, ACTION_DELETE];
    }, []);

    const renderTopic = useCallback((topic, idx) => {
        const createdAt = moment(topic.created_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY/MM/DD HH:mm:ss');
        const updatedAt = moment(topic.updated_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY/MM/DD HH:mm:ss');
        return <tr key={idx}>
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {topic.title}
            </td >
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {topic.workspace}
            </td >
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {topic.categoryName}
            </td>
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {topic.name}
            </td>
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {createdAt}
            </td>
            <td onClick={()=> {
                history.push(getURLTopic(topic))
            }}>
                {updatedAt}
            </td>
            <td>
                <DropdownMenu position={'right'}
                              list={renderListWithRole(topic, role)}
                              getOptionLabel={(option) => option}
                              onChange={(option) => onChooseAction(option, topic)}>
                    <img src="/img/more_vert_24px.png" className={'more-button-action'} alt="more"/>
                </DropdownMenu>
            </td>
        </tr>
    }, [role]);

    return <>
        <div className="wrap-my-topic">
            <div className="title-my-topic">
                <h1>My Topics
                    <span>
                        <i className="fa fa-question-circle"></i>
                        <div className='tooltips-my-topic'><p>ゲーム・球団・選手ページに対し、閲覧したい項目を抽出。メモのように使える自分だけのTopicsを作成し、保存できます。</p></div>
                    </span>
                </h1>
                <div className="title-right">
                    <div className="search ">
                        <DebounceInput
                            style={{outline: 'none'}}
                            placeholder="検索"
                            minLength={0}
                            debounceTimeout={300}
                            onChange={e => setKeyword(e.target.value)} />
                        <img alt="search" src="/img/ic_search_svg.svg"/>
                    </div>
                    <div>
                        <DropdownMenu list={listCreateTopic}
                                      getOptionLabel={(option) => option}
                                      onChange={onChooseOption}>
                            <button className='btn-add-topic'>
                                <img src="/img/ic_add_svg.svg" alt="add"/>
                                Topicsを作成
                            </button>
                        </DropdownMenu>
                    </div>
                </div>
            </div>
            <div className="content-my-topic">
                <div className='content-my-topic-container'>
                    <Pagination onChangePage={onChangePage}
                                totalPages={totalPage}
                                initialPage={currentPage}
                                pageSize={itemPerPage}
                                showPagination={true}
                    />
                    <div>
                        <table className='table-my-topic'>
                            <thead>
                                <tr>
                                    {headers.map((header, index) => <th key={header.title + index}
                                        style={{ width: header.width }}
                                        onClick={() => onSortField(header.field)}>
                                        <span style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}>
                                            {sortField.field === header.field && <img alt="download" src={'/img/ic_arrow_leader_board.svg'} style={{
                                                transform: sortField.order === 'asc' && 'rotate(180deg)',
                                            }}/>}
                                            {header.title}
                                        </span>
                                    </th>)}
                                    <th style={{width: '5%'}}/>
                                </tr>
                            </thead>
                            <tbody>
                                {listTopic.map((topic, idx) => renderTopic(topic, idx))}
                            </tbody>
                        </table>
                    </div>
                    {error && <div className={'message-error'}>
                        {error}
                    </div>}
                    <Pagination onChangePage={onChangePage}
                                totalPages={totalPage}
                                initialPage={currentPage}
                                pageSize={itemPerPage}
                                showPagination={true}
                    />
                </div>
            </div>
        </div>

        {selectedTopic === TOPIC_PLAYER && <TopicChooseUser
                                                onChange={onCreateTopic}
                                                onClose={onCloseModal} />}

        {selectedTopic === TOPIC_TEAM && <TopicChooseTeam
                                                onChange={onCreateTopic}
                                                onClose={onCloseModal} />}

        {selectedTopic === TOPIC_GAME && <TopicChooseGame
                                                onChange={onCreateTopic}
                                                onClose={onCloseModal} />}

        {selectedAction === ACTION_DELETE && <TopicDelete
                                                item={selectedRow}
                                                onClose={onCloseActionModal}
                                                onSave={onDeleteTopic}/>}

        {selectedAction === ACTION_COPY_WORKSPACE && <TopicCopyWorkspace
                                                onClose={onCloseActionModal}
                                                onSave={onSaveCopyWorkspace}/>}

        <Loading show={createTopicLoading} />
    </>
}

export default MyTopic;